import React from 'react'
import { useTranslation } from 'react-i18next'
import './TablePager.css'

type TablePagerProps = {
  page: number
  pageSize: number
  total: number
  pageCount: number
  from: number
  to: number
  pageSizeOptions?: readonly number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

/** Branded pagination footer for client-side tables. */
export default function TablePager({
  page,
  pageSize,
  total,
  pageCount,
  from,
  to,
  pageSizeOptions = [10, 25, 50],
  onPageChange,
  onPageSizeChange,
}: TablePagerProps) {
  const { t } = useTranslation()
  if (total <= 0) return null

  return (
    <div className="incyia-pager" role="navigation" aria-label={t('pagination', { defaultValue: 'Pagination' })}>
      <div className="incyia-pager-range">
        {t('tablePager.range', {
          from,
          to,
          total,
          defaultValue: `${from}–${to} of ${total}`,
        })}
      </div>

      <div className="incyia-pager-controls">
        <label className="incyia-pager-size">
          <span>{t('tablePager.rowsPerPage', { defaultValue: 'Rows' })}</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label={t('tablePager.rowsPerPage', { defaultValue: 'Rows' })}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <div className="incyia-pager-btns">
          <button
            type="button"
            className="incyia-pager-btn"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label={t('tablePager.prev', { defaultValue: 'Previous page' })}
          >
            ‹
          </button>
          <span className="incyia-pager-page">
            {t('tablePager.pageOf', {
              page,
              pageCount,
              defaultValue: `${page} / ${pageCount}`,
            })}
          </span>
          <button
            type="button"
            className="incyia-pager-btn"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            aria-label={t('tablePager.next', { defaultValue: 'Next page' })}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  )
}
